import axios from './transport';
class ApiError extends Error {
  constructor(status, message, detail) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.name = "ApiError";
  }
  status;
  detail;
}
class OfflineError extends Error {
  constructor(cause) {
    super("The reporting service is not responding. Please try again shortly.");
    this.name = "OfflineError";
    this.cause = cause;
  }
}
// Public API address; override only when testing a different reporting service.
const API_BASE = process.env.REACT_APP_ARP_API_URL || 'https://adr-api.instapm.ca';
function sharedHeaders() {
  const token = localStorage.getItem('token');
  const session = sessionStorage.getItem('sessionID');
  return { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(session ? { 'x-sessionid': session } : {}) };
}
let bearer = null;
function setToken(token) {
  bearer = token;
}
function getToken() {
  return bearer;
}
async function request(path, init) {
  const headers = { ...init?.headers ?? {} };
  if (init?.body) headers["content-type"] = "application/json";
  Object.assign(headers, sharedHeaders());
  let res;
  try {
    res = await axios({ baseURL: API_BASE, timeout: 30000, url: path, method: init?.method || "GET", data: init?.body, headers, responseType: "text", transformResponse: [data => data] });
  } catch (err) {
    if (err.response) res = err.response; else throw new OfflineError(err);
  }
  if (res.status === 204) return void 0;
  const text = res.data;
  let body;
  try {
    body = text ? JSON.parse(text) : void 0;
  } catch {
    throw new ApiError(res.status, text.slice(0, 300) || `${res.status} ${res.statusText}`);
  }
  if (res.status < 200 || res.status >= 300) {
    const shaped = body;
    throw new ApiError(res.status, shaped?.error ?? `${res.status} ${res.statusText}`, shaped?.detail);
  }
  return body;
}
const api = {
  features: () => request("/api/me/features"),
  updateFeatures: (enabled) => request("/api/me/features", { method: "PATCH", body: JSON.stringify({ adrBetaOptIn: enabled }) }),
  ackEvidence: (id) => request(`/api/submissions/${id}/ack-evidence`),
  importAckEvidence: (id, base64) => request(`/api/submissions/${id}/ack-evidence`, { method: "POST", body: JSON.stringify({ base64 }) }),
  ackEvidenceXmlUrl: (id) => `/api/submissions/${id}/ack-evidence/xml`,
  health: () => request("/api/health"),
  /* -- sign-in -- */
  authConfig: () => request("/api/auth/config"),
  signIn: (username, password) => request("/api/auth/sign-in", {
    method: "POST",
    body: JSON.stringify({ username, password })
  }),
  signOut: () => request("/api/auth/sign-out", { method: "POST" }),
  /* -- the person and the company -- */
  me: () => request("/api/me"),
  updateMe: (patch) => request("/api/me", { method: "PATCH", body: JSON.stringify(patch) }),
  company: () => request("/api/company"),
  updateCompany: (patch) => request("/api/company", { method: "PATCH", body: JSON.stringify(patch) }),
  members: () => request("/api/company/members").then((r) => r.members),
  updateMember: (id, patch) => request(`/api/company/members/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deactivateMember: (id) => request(`/api/company/members/${id}`, { method: "DELETE" }),
  /* -- reports -- */
  /** The dashboard's page. Empty and default values are left off the query. */
  list: (query = {}) => {
    const params = new URLSearchParams();
    if (query.page && query.page > 1) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    if (query.status && query.status !== "all") params.set("status", query.status);
    if (query.sort) params.set("sort", query.sort);
    const qs = params.toString();
    return request(`/api/files${qs ? `?${qs}` : ""}`);
  },
  get: (id) => request(`/api/files/${id}`),
  create: (seed) => request("/api/files", { method: "POST", body: JSON.stringify({ seed }) }),
  createFrom: (model, title, source = {}) => request("/api/files", { method: "POST", body: JSON.stringify({ model, title, ...source }) }),
  source: (id) => request(`/api/files/${id}/source`).then(result => result.source),
  sourceXmlUrl: (id) => `/api/files/${id}/source/xml`,
  save: (id, model, revision) => request(`/api/files/${id}`, {
    method: "PUT",
    body: JSON.stringify({ model, revision })
  }),
  remove: (id) => request(`/api/files/${id}`, { method: "DELETE" }),
  revisions: (id) => request(`/api/files/${id}/revisions`).then((r) => r.revisions),
  revision: (id, n) => request(`/api/files/${id}/revisions/${n}`),
  validate: (model) => request("/api/validate", { method: "POST", body: JSON.stringify({ model }) }),
  submissions: (fileId) => request(`/api/files/${fileId}/submissions`).then((r) => r.submissions),
  prepareSubmission: (fileId) => request(`/api/files/${fileId}/submissions`, { method: "POST" }),
  advanceSubmission: (id, patch) => request(`/api/submissions/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  /** URLs, not fetches — these are hrefs the browser downloads directly. */
  xmlUrl: (fileId) => `/api/files/${fileId}/xml`,
  submissionXmlUrl: (id) => `/api/submissions/${id}/xml`
};
export {
  ApiError,
  OfflineError,
  api,
  getToken,
  setToken
};

export async function downloadXml(path) {
  let res;
  try {
    res = await axios({ baseURL: API_BASE, timeout: 30000, url: path, headers: sharedHeaders(), responseType: "blob" });
  } catch (err) {
    if (!err.response) throw new OfflineError(err);
    let body;
    try { body = JSON.parse(await err.response.data.text()); } catch { /* Non-JSON failure. */ }
    const findings = body?.detail?.caseHistory || body?.detail?.errors || [];
    const explanation = findings.map(f => f.message).filter(Boolean).join(' ');
    throw new ApiError(err.response.status, [body?.error || 'The XML download failed.', explanation].filter(Boolean).join(' '), body?.detail);
  }
  if (res.status < 200 || res.status >= 300) throw new Error('The XML download failed. Please check your session and try again.');
  const blob = res.data; const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url;
  a.download = res.headers['content-disposition']?.match(/filename="?([^";]+)/)?.[1] || 'adverse-report.xml';
  a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
}
